import { useRef, useLayoutEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import type { ViewOption } from "./types";
import { Button } from "../primitives";

interface PillStyle {
  left: number;
  width: number;
}

export function ViewToggle({
  options,
  onViewChange,
}: {
  options?: ViewOption[];
  onViewChange?: (id: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [pill, setPill] = useState<PillStyle | null>(null);
  const [isInitial, setIsInitial] = useState(true);

  const allOptions = options ?? [];
  const defaultOptions = allOptions.filter((o) => o.variant !== "cta");
  const ctaOptions = allOptions.filter((o) => o.variant === "cta");
  const activeOption = defaultOptions.find((o) => o.active);

  const setButtonRef = useCallback(
    (id: string) => (el: HTMLButtonElement | null) => {
      if (el) {
        buttonRefs.current.set(id, el);
      } else {
        buttonRefs.current.delete(id);
      }
    },
    [],
  );

  const activeId = activeOption?.id;

  useLayoutEffect(() => {
    if (!activeId || !containerRef.current) return;

    const button = buttonRefs.current.get(activeId);
    if (!button) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();

    const left = buttonRect.left - containerRect.left;
    const width = buttonRect.width;

    setPill((prev) => {
      if (prev && prev.left === left && prev.width === width) return prev;
      return { left, width };
    });

    // After the first measurement, allow transitions
    requestAnimationFrame(() => {
      setIsInitial(false);
    });
  }, [activeId]);

  return (
    <div className="flex items-center gap-1">
      {/* Toggle group with sliding pill */}
      <div ref={containerRef} className="relative flex items-center gap-1">
        {/* Sliding pill background */}
        {pill && (
          <div
            className="absolute top-0 bottom-0 rounded-button bg-primary"
            style={{
              left: pill.left,
              width: pill.width,
              transition: isInitial
                ? "none"
                : "left 300ms cubic-bezier(0.4, 0, 0.2, 1), width 300ms cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          />
        )}

        {/* Toggle buttons */}
        {defaultOptions.map((option) => {
          const Icon = option.icon;
          return (
            <motion.button
              key={option.id}
              ref={setButtonRef(option.id)}
              onClick={() => onViewChange?.(option.id)}
              whileTap={{ scale: 0.95 }}
              className={`relative z-10 flex items-center gap-1.5 rounded-button px-3 py-1.5 text-[14px] font-medium transition-colors duration-200 ${
                option.active
                  ? "text-white"
                  : "text-primary  bg-white hover:text-primary hover:bg-card-hover"
              }`}
            >
              {Icon && <Icon className="h-3.5 w-3.5" />}
              {option.label}
            </motion.button>
          );
        })}
      </div>

      {/* CTA buttons (outside the toggle group) */}
      {ctaOptions.map((option) => (
        <span key={option.id} className="flex items-center gap-2.5 ml-1.5">
          <div className="h-5 w-px bg-[#E5E5EA]" />
          <Button
            variant="primary"
            icon={option.icon}
            onClick={() => onViewChange?.(option.id)}
            className="px-3.5"
          >
            {option.label}
          </Button>
        </span>
      ))}
    </div>
  );
}
